// shared-lib/cabinet-runs/reparent.js
//
// ONE re-parenting rule for objects that name a countertop (#1527 — this
// lived, byte-identical, in materializeScanRuns, the web run engine, and
// post-processing's findHostCountertop; three copies of the rule that
// decides which slab a sink lands on is exactly how a re-solve strands a
// sink on a different slab than post-processing chose).
//
// Why it matters: a run OWNS its countertops, so a re-solve deletes the old
// slab and emits a new one with a new id — but the sink object is not
// run-owned and survives, still naming the slab that went away. That is not
// cosmetic: the scene skips every sink carrying a countertop parent (the
// Countertop component draws it, cut into the slab) and matches on the
// exact parent id — a sink pointing at a deleted countertop is drawn by
// NEITHER path and vanishes. Faucets are slab-parented too, so the rule is
// general rather than sink-shaped.

/**
 * The countertop whose footprint covers `sink`, or the nearest within
 * 0.5 m: transform the object's XZ into the slab's local frame and test the
 * half-extents, falling back to the least overshoot.
 */
function hostCountertopFor(sink, countertops) {
  if (!sink?.center || !countertops.length) return null;
  let best = null;
  let bestDist = Infinity;
  for (const c of countertops) {
    if (!c.center || !c.dimensions) continue;
    const dx = sink.center.x - c.center.x;
    const dz = sink.center.z - c.center.z;
    const ry = c.rotation?.y || 0;
    const cosR = Math.cos(ry);
    const sinR = Math.sin(ry);
    const localX = dx * cosR - dz * sinR;
    const localZ = dx * sinR + dz * cosR;
    const halfX = (c.dimensions.x || 0) / 2;
    const halfZ = (c.dimensions.z || 0) / 2;
    if (Math.abs(localX) <= halfX + 0.01 && Math.abs(localZ) <= halfZ + 0.01) {
      return c;
    }
    const d = Math.hypot(
      Math.max(0, Math.abs(localX) - halfX),
      Math.max(0, Math.abs(localZ) - halfZ)
    );
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return bestDist <= 0.5 ? best : null;
}

/**
 * Point everything that names a countertop at one that still exists; strip
 * the parent when nothing covers it (un-cut beats invisible).
 */
function reparentOntoCountertops(objects) {
  const countertops = objects.filter((o) => o.type === 'countertop');
  const liveIds = new Set(objects.filter((o) => o?.id).map((o) => o.id));
  return objects.map((o) => {
    if (!Array.isArray(o.parents)) return o;
    const idx = o.parents.findIndex((p) => p?.type === 'countertop');
    if (idx < 0 || liveIds.has(o.parents[idx].id)) return o;
    const host = hostCountertopFor(o, countertops);
    const parents = host
      ? o.parents.map((p, i) => (i === idx ? { ...p, id: host.id } : p))
      : o.parents.filter((_, i) => i !== idx);
    return { ...o, parents };
  });
}

module.exports = { hostCountertopFor, reparentOntoCountertops };
